// Monta o head() de cada rota (title, description, canonical, Open Graph e
// JSON-LD) num formato único, para nenhuma página esquecer uma tag.

export const SITE_URL = "https://nutriclaracamille.com.au";
export const SITE_NAME = "Clara Camille";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

type JsonLd = Record<string, unknown>;

interface PageSeo {
  title: string;
  description?: string;
  /** Caminho da página, com barra inicial. Vira canonical e og:url. */
  path: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  /** Impede a indexação (ferramentas internas, páginas de obrigado etc.). */
  noindex?: boolean;
  /** Blocos JSON-LD específicos da página. O ProfessionalService global vem da rota raiz. */
  jsonLd?: JsonLd[];
  /** Metas extras, por exemplo article:published_time. */
  extraMeta?: Array<Record<string, string>>;
}

export const pageHead = ({
  title,
  description,
  path,
  ogTitle,
  ogDescription,
  ogImage,
  ogType,
  noindex,
  jsonLd = [],
  extraMeta = [],
}: PageSeo) => {
  const url = `${SITE_URL}${path}`;

  const meta: Array<Record<string, string>> = [{ title }];
  if (description) meta.push({ name: "description", content: description });
  if (noindex) meta.push({ name: "robots", content: "noindex, nofollow" });

  meta.push(
    { property: "og:title", content: ogTitle ?? title },
    { property: "og:url", content: url },
    { name: "twitter:title", content: ogTitle ?? title },
  );
  const ogDesc = ogDescription ?? description;
  if (ogDesc) {
    meta.push({ property: "og:description", content: ogDesc }, { name: "twitter:description", content: ogDesc });
  }
  if (ogImage) {
    meta.push({ property: "og:image", content: ogImage }, { name: "twitter:image", content: ogImage });
  }
  if (ogType) meta.push({ property: "og:type", content: ogType });
  meta.push(...extraMeta);

  return {
    meta,
    // Páginas noindex não precisam de canonical; evita apontar o Google para elas.
    links: noindex ? [] : [{ rel: "canonical", href: url }],
    scripts: jsonLd.map((data) => ({
      type: "application/ld+json",
      children: JSON.stringify(data),
    })),
  };
};

/** Gera o schema FAQPage a partir da lista de perguntas de uma página. */
export const faqJsonLd = (items: Array<{ question: string; answer: string }>): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
});

/** Gera o schema BreadcrumbList: Início → página atual. */
export const breadcrumbJsonLd = (trail: Array<{ name: string; path: string }>): JsonLd => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [{ name: "Início", path: "/" }, ...trail].map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});
